import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { exportChapterPdf } from "@/lib/pdf";
import { ArrowLeft, ArrowRight, Download, Check } from "lucide-react";
import PaywallCard from "@/components/PaywallCard";

export default function ChapterReader() {
  const { track, chapterId } = useParams();
  const { user } = useAuth();
  const [chapter, setChapter] = useState(null);
  const [trackData, setTrackData] = useState(null);
  const [progress, setProgress] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/guide/${track}/${chapterId}`),
      api.get(`/guide/${track}`),
    ]).then(([ch, tr]) => {
      setChapter(ch.data); setTrackData(tr.data);
    }).catch(() => setErr("Could not load this chapter."));
    if (user) {
      api.get("/progress").then((r) => setProgress(r.data.items || {})).catch(() => {});
    }
  }, [track, chapterId, user]);

  const toggleItem = async (i, current) => {
    if (!user) return;
    const key = `${track}__${chapterId}__${i}`;
    setProgress((p) => ({ ...p, [key]: !current }));
    try {
      const r = await api.put("/progress", { track, chapter_id: chapterId, item_index: i, checked: !current });
      setProgress(r.data.items || {});
    } catch { setProgress((p) => ({ ...p, [key]: current })); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#B05A5A]">{err}</div>;
  if (!chapter || !trackData) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#8A9488]">Loading…</div>;

  const idx = trackData.chapters.findIndex((c) => c.id === chapterId);
  const prev = idx > 0 ? trackData.chapters[idx - 1] : null;
  const next = idx < trackData.chapters.length - 1 ? trackData.chapters[idx + 1] : null;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <Link to={`/guide/${track}`} data-testid="back-to-track" className="inline-flex items-center gap-1.5 text-sm text-[#6B7F62] hover:underline mb-6">
        <ArrowLeft size={16} /> {trackData.title}
      </Link>

      <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-3">Chapter {idx + 1} of {trackData.chapters.length}</div>
      <h1 data-testid="chapter-title" className="font-serif text-4xl md:text-5xl text-[#2D332D] leading-tight mb-4">
        {chapter.title}
      </h1>
      <p className="text-lg text-[#576056] leading-relaxed mb-2">{chapter.summary}</p>
      <div className="text-sm text-[#8A9488] mb-10">{chapter.reading_time}</div>

      {chapter.locked ? (
        <PaywallCard
          title="This chapter is part of the Premium plan."
          subtitle="It's honestly a small kindness — a one-time £29 unlocks this chapter, the two others, the Plan Builder, and the AI companion. All bereavement content stays free forever."
          features={[
            "Funeral wishes · Your admin system · Letters to loved ones",
            "Full Plan Builder with PDF export",
            "Unlimited AI companion chat",
            "One-time purchase — no subscription",
          ]}
        />
      ) : (
        <>
          <div className="prose-warm">
            {chapter.sections.map((s, i) => (
              <div key={i} className="reveal" style={{ animationDelay: `${i * 60}ms` }}>
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>

          {chapter.checklist?.length > 0 && (
            <div className="mt-14">
              <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-4">Checklist</div>
              <div className="space-y-2" data-testid="chapter-checklist">
                {chapter.checklist.map((item, i) => {
                  const key = `${track}__${chapterId}__${i}`;
                  const checked = !!progress[key];
                  return (
                    <button
                      key={i}
                      onClick={() => toggleItem(i, checked)}
                      data-testid={`checklist-item-${i}`}
                      disabled={!user}
                      className={`w-full flex items-start gap-4 p-4 rounded-xl border text-left transition-colors ${
                        checked
                          ? "border-[#9EAD94] bg-[#E9ECE8]/60"
                          : "border-[#EAE6E1] bg-white hover:border-[#9EAD94]"
                      } ${!user ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                    >
                      <div
                        className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          checked ? "bg-[#6B7F62] border-[#6B7F62]" : "bg-white border-[#D4CEC4]"
                        }`}
                      >
                        {checked && <Check size={13} className="text-white" strokeWidth={3} />}
                      </div>
                      <span className={`text-[15px] ${checked ? "text-[#576056] line-through" : "text-[#2D332D]"}`}>{item}</span>
                    </button>
                  );
                })}
              </div>
              {!user && (
                <div className="mt-4 text-sm text-[#8A9488]">
                  <Link to="/register" className="text-[#6B7F62] font-medium hover:underline">Create a free account</Link> to save your progress across sessions.
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="mt-14 pt-8 border-t border-[#EAE6E1] flex items-center justify-between flex-wrap gap-4">
        <button
          data-testid="download-chapter-pdf"
          onClick={() => exportChapterPdf(track, chapter, user)}
          className="btn-secondary text-sm"
        >
          <Download size={16} /> Save chapter as PDF
        </button>
        <div className="flex gap-3">
          {prev && (
            <Link to={`/guide/${track}/${prev.id}`} data-testid="prev-chapter" className="btn-ghost text-sm">
              <ArrowLeft size={16} /> {prev.title}
            </Link>
          )}
          {next && (
            <Link to={`/guide/${track}/${next.id}`} data-testid="next-chapter" className="btn-primary text-sm">
              {next.title} <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
